import { apiGet } from "../../services/genesisApi/httpsMethods";
import type { DealTemplateVariantOption } from "./DealTemplateCard";

const DEAL_TEMPLATES_ENDPOINT = "deal-templates";

export type DealTemplateVariantRow = {
  template_key: string;
  variant_slug: string;
  display_name: string;
  current_blob_filename?: string;
  current_version?: number;
  last_uploaded_at?: string;
  last_uploaded_by_email?: string;
};

export type DealTemplateVersionKey =
  | "term_sheet_email"
  | "ten_pager"
  | "forward_rate_curves";

function extractVariantRows(
  body: DealTemplateVariantRow[] | { items: DealTemplateVariantRow[] } | undefined,
): DealTemplateVariantRow[] {
  if (!body) return [];
  if (Array.isArray(body)) return body;
  if (Array.isArray(body.items)) return body.items;
  return [];
}

/** Strip leading "Ten pager —" style prefix from API display names for UI labels. */
export function formatTenPagerVariantLabel(displayName: string): string {
  const trimmed = displayName.trim();
  const withoutPrefix = trimmed.replace(/^ten\s*pager\s*[—–\-:\s]+\s*/i, "").trim();
  return withoutPrefix || trimmed;
}

/** GET /deal-templates/variants?template_key=ten_pager */
export async function fetchDealTemplateVariants(
  templateKey = "ten_pager",
): Promise<DealTemplateVariantOption[]> {
  const response = await apiGet<
    DealTemplateVariantRow[] | { items: DealTemplateVariantRow[] }
  >(`${DEAL_TEMPLATES_ENDPOINT}/variants`, { template_key: templateKey });

  if (!response.success) return [];

  return extractVariantRows(response.data).map((row) => ({
    slug: row.variant_slug,
    label: formatTenPagerVariantLabel(row.display_name),
  }));
}

/** Convert a user-entered label into an API variant slug. */
export function slugifyVariantInput(input: string): string {
  const slug = input
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return slug || "default";
}

export function templateApiBasePath(templateApiKey: DealTemplateVersionKey): string {
  return `${DEAL_TEMPLATES_ENDPOINT}/${templateApiKey}`;
}

/** Query param for ten pager ops; omit for `default` (API backward compat). */
export function tenPagerVariantQueryParam(
  variantSlug?: string,
): Record<string, string> | undefined {
  if (!variantSlug || variantSlug === "default") return undefined;
  return { variant: variantSlug };
}

export function withQueryParams(
  path: string,
  queryParams?: Record<string, string>,
): string {
  if (!queryParams || Object.keys(queryParams).length === 0) return path;
  const search = new URLSearchParams(queryParams).toString();
  return `${path}?${search}`;
}

export function tenPagerStateKey(variantSlug: string): string {
  return `ten_pager/${variantSlug}`;
}
